import { useEffect, useState } from "react";
import { PageSkeleton } from "@/components/PageSkeleton";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatIDR, formatDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Gift, CheckCircle2, Clock, Search, ThumbsUp, XCircle, Ban } from "lucide-react";

const ACCOUNTS = ["Bank BCA", "Bank Mandiri", "Bank BRI", "QRIS / E-Wallet", "Kas Tunai"];

const TABS = [
  ["pending", "Menunggu Persetujuan"],
  ["approved", "Disetujui"],
  ["partial", "Dicairkan Sebagian"],
  ["disbursed", "Sudah Dicairkan"],
  ["cancelled", "Dibatalkan"],
];

const STATUS_STYLE = {
  "Menunggu Persetujuan": "bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-950 dark:text-amber-300",
  "Disetujui": "bg-sky-100 text-sky-800 border-sky-300 dark:bg-sky-950 dark:text-sky-300",
  "Dicairkan Sebagian": "bg-indigo-100 text-indigo-800 border-indigo-300 dark:bg-indigo-950 dark:text-indigo-300",
  "Sudah Dicairkan": "bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-950 dark:text-emerald-300",
  "Dibatalkan": "bg-rose-100 text-rose-800 border-rose-300 dark:bg-rose-950 dark:text-rose-300",
};

export default function Cashback() {
  const { user } = useAuth();
  const canManage = user.role === "owner" || (user.role === "admin" && user.can_disburse_cashback);
  const [status, setStatus] = useState("pending");
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [disburseTarget, setDisburseTarget] = useState(null);
  const [account, setAccount] = useState("Bank BCA");
  const [disburseMode, setDisburseMode] = useState("full");
  const [disburseAmount, setDisburseAmount] = useState("");
  const [cancelTarget, setCancelTarget] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/cashback?status=${status}${q ? `&search=${encodeURIComponent(q)}` : ""}`).then((r) => setData(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, q]);

  const approve = async (c) => {
    try {
      await api.post(`/transactions/${c.id}/approve-cashback`);
      toast.success("Cashback disetujui — siap dicairkan");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const disburseRemaining = disburseTarget ? (disburseTarget.cashback || 0) - (disburseTarget.cashback_disbursed_amount || 0) : 0;

  const disburse = async () => {
    let amt;
    if (disburseMode === "partial") {
      amt = parseFloat(disburseAmount);
      if (!amt || amt <= 0) { toast.error("Isi nominal pencairan sebagian"); return; }
      if (amt > disburseRemaining) { toast.error(`Nominal melebihi sisa cashback (${formatIDR(disburseRemaining)})`); return; }
    }
    setBusy(true);
    try {
      await api.post(`/transactions/${disburseTarget.id}/disburse-cashback`, { account, ...(disburseMode === "partial" ? { amount: amt } : {}) });
      toast.success("Cashback dicairkan");
      setDisburseTarget(null); setAccount("Bank BCA"); setDisburseMode("full"); setDisburseAmount("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const cancel = async () => {
    if (!reason.trim()) { toast.error("Alasan pembatalan wajib diisi"); return; }
    setBusy(true);
    try {
      await api.post(`/transactions/${cancelTarget.id}/cancel-cashback`, { reason: reason.trim() });
      toast.success("Cashback dibatalkan");
      setCancelTarget(null); setReason("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!data) return <PageSkeleton stats={4} rows={5} />;

  const stats = [
    ["Belum Dicairkan", data.total_pending + data.total_approved, Clock, "amber"],
    ["Menunggu Persetujuan", data.total_pending, ThumbsUp, "sky"],
    ["Sudah Dicairkan", data.total_disbursed, CheckCircle2, "emerald"],
    ["Dibatalkan", data.total_cancelled, Ban, "rose"],
  ];
  const tone = {
    amber: ["bg-amber-100 dark:bg-amber-950", "text-amber-600"],
    sky: ["bg-sky-100 dark:bg-sky-950", "text-sky-600"],
    emerald: ["bg-emerald-100 dark:bg-emerald-950", "text-emerald-600"],
    rose: ["bg-rose-100 dark:bg-rose-950", "text-rose-600"],
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-heading text-lg sm:text-xl font-bold tracking-tight">Cashback</h2>
        <p className="text-xs sm:text-sm text-muted-foreground">Cashback adalah kewajiban setelah pelanggan membayar lunas — bukan potongan harga. Harus disetujui sebelum dicairkan.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map(([label, val, Icon, color]) => (
          <Card key={label} data-testid={`cashback-stat-${color}`} className="p-4">
            <div className="flex items-center gap-2.5">
              <div className={`grid place-items-center h-9 w-9 rounded-lg shrink-0 ${tone[color][0]}`}><Icon className={`h-4 w-4 ${tone[color][1]}`} /></div>
              <div><p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p><p className="font-heading text-lg font-extrabold">{formatIDR(val)}</p></div>
            </div>
          </Card>
        ))}
      </div>

      <Card className="overflow-hidden">
        <div className="p-4 border-b border-border flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
          <div className="inline-flex flex-wrap gap-1 rounded-xl bg-secondary p-1">
            {TABS.map(([id, lbl]) => (
              <Button key={id} size="sm" variant={status === id ? "default" : "ghost"} onClick={() => setStatus(id)}
                data-testid={`cashback-filter-${id}`} className={`h-8 px-3 text-xs ${status === id ? "shadow-sm" : "text-muted-foreground"}`}>{lbl}</Button>
            ))}
          </div>
          <div className="relative max-w-xs w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input data-testid="cashback-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari pelanggan" className="pl-9" />
          </div>
        </div>
        <div className="hidden lg:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tanggal</TableHead><TableHead>Pelanggan</TableHead><TableHead>Admin</TableHead><TableHead>Kategori</TableHead>
                <TableHead className="text-right">Harga Jual</TableHead><TableHead className="text-right">Cashback</TableHead>
                <TableHead>Status</TableHead><TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-10">Tidak ada data cashback.</TableCell></TableRow>}
              {data.items.map((c) => (
                <TableRow key={c.id} data-testid={`cashback-row-${c.id}`}>
                  <TableCell className="text-sm whitespace-nowrap">{formatDate(c.date)}</TableCell>
                  <TableCell className="font-medium">{c.customer_name}</TableCell>
                  <TableCell className="text-sm text-muted-foreground" data-testid={`cashback-admin-${c.id}`}>{c.created_by_name || "-"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{c.category}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">{formatIDR(c.amount)}</TableCell>
                  <TableCell className="text-right font-semibold text-amber-600 whitespace-nowrap">{formatIDR(c.cashback)}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={STATUS_STYLE[c.cashback_status] || ""}>{c.cashback_status}</Badge>
                    {(c.cashback_status === "Menunggu Persetujuan" || c.cashback_status === "Disetujui" || c.cashback_status === "Dicairkan Sebagian") && (
                      <span className="block text-[10px] text-muted-foreground mt-0.5 whitespace-nowrap" data-testid={`cashback-conds-${c.id}`}>Bayar: {c.payment_status} · Kirim: {c.delivery_status || "Belum Dikirim"}</span>
                    )}
                    {(c.cashback_status === "Disetujui" || c.cashback_status === "Dicairkan Sebagian") && (
                      c.can_disburse
                        ? <span className="block text-[10px] text-emerald-600 mt-0.5 font-semibold" data-testid={`cashback-ready-${c.id}`}>Siap Dicairkan</span>
                        : <span className="block text-[10px] text-rose-500 mt-0.5 max-w-[170px] whitespace-normal" data-testid={`cashback-blocked-${c.id}`}>{c.block_reason}</span>
                    )}
                    {c.cashback_status === "Dibatalkan" && c.cashback_cancel_reason &&
                      <span className="block text-[10px] text-muted-foreground mt-0.5 max-w-[160px] whitespace-normal">Alasan: {c.cashback_cancel_reason}</span>}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex gap-1.5 justify-end">
                      {canManage && c.cashback_status === "Menunggu Persetujuan" && (
                        <Button size="sm" variant="outline" className="h-8 gap-1" onClick={() => approve(c)} data-testid={`cashback-approve-${c.id}`}>
                          <ThumbsUp className="h-3.5 w-3.5" /> Setujui
                        </Button>
                      )}
                      {canManage && (c.cashback_status === "Disetujui" || c.cashback_status === "Dicairkan Sebagian") && c.can_disburse && (
                        <Button size="sm" variant="gold" className="h-8 gap-1" onClick={() => { setDisburseTarget(c); setAccount("Bank BCA"); setDisburseMode("full"); setDisburseAmount(""); }} data-testid={`cashback-disburse-${c.id}`}>
                          <Gift className="h-3.5 w-3.5" /> {c.cashback_status === "Dicairkan Sebagian" ? "Cairkan Sisa" : "Cairkan"}
                        </Button>
                      )}
                      {canManage && (c.cashback_status === "Menunggu Persetujuan" || c.cashback_status === "Disetujui") && (
                        <Button size="sm" variant="ghost" className="h-8 gap-1 text-rose-600 hover:text-rose-700" onClick={() => { setCancelTarget(c); setReason(""); }} data-testid={`cashback-cancel-${c.id}`}>
                          <XCircle className="h-3.5 w-3.5" /> Batalkan
                        </Button>
                      )}
                      {c.cashback_status === "Sudah Dicairkan" && <span className="text-xs text-muted-foreground">oleh {c.cashback_by || "-"}</span>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="lg:hidden divide-y divide-border">
          {data.items.length === 0 && <p className="text-center text-muted-foreground text-xs py-8">Tidak ada data cashback.</p>}
          {data.items.map((c) => (
            <div key={c.id} data-testid={`cashback-row-${c.id}`} className="p-3 space-y-1.5">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold">{c.customer_name}</p>
                  <p className="text-[11px] text-muted-foreground">{formatDate(c.date)} · {c.category} · Admin: {c.created_by_name || "-"}</p>
                </div>
                <p className="text-sm font-bold text-amber-600 whitespace-nowrap">{formatIDR(c.cashback)}</p>
              </div>
              <div className="flex items-center justify-between gap-2 text-[11px]">
                <span className="text-muted-foreground">Harga Jual: {formatIDR(c.amount)}</span>
                <Badge variant="outline" className={`${STATUS_STYLE[c.cashback_status] || ""} text-[10px] px-1.5 py-0`}>{c.cashback_status}</Badge>
              </div>
              {(c.cashback_status === "Menunggu Persetujuan" || c.cashback_status === "Disetujui" || c.cashback_status === "Dicairkan Sebagian") && (
                <p className="text-[10px] text-muted-foreground" data-testid={`cashback-conds-${c.id}`}>Bayar: {c.payment_status} · Kirim: {c.delivery_status || "Belum Dikirim"}</p>
              )}
              {(c.cashback_status === "Disetujui" || c.cashback_status === "Dicairkan Sebagian") && (
                c.can_disburse
                  ? <p className="text-[10px] text-emerald-600 font-semibold" data-testid={`cashback-ready-${c.id}`}>Siap Dicairkan</p>
                  : <p className="text-[10px] text-rose-500" data-testid={`cashback-blocked-${c.id}`}>{c.block_reason}</p>
              )}
              {c.cashback_status === "Dibatalkan" && c.cashback_cancel_reason && (
                <p className="text-[10px] text-muted-foreground break-words">Alasan: {c.cashback_cancel_reason}</p>
              )}
              {c.cashback_status === "Sudah Dicairkan" && (
                <p className="text-[10px] text-muted-foreground">oleh {c.cashback_by || "-"}</p>
              )}
              {canManage && (c.cashback_status === "Menunggu Persetujuan" || c.cashback_status === "Disetujui") && (
                <div className="flex justify-end gap-1 pt-0.5">
                  {c.cashback_status === "Menunggu Persetujuan" && (
                    <Button size="sm" variant="outline" className="h-7 text-xs gap-1 px-2" onClick={() => approve(c)} data-testid={`cashback-approve-${c.id}`}>
                      <ThumbsUp className="h-3 w-3" /> Setujui
                    </Button>
                  )}
                  {(c.cashback_status === "Disetujui" || c.cashback_status === "Dicairkan Sebagian") && c.can_disburse && (
                    <Button size="sm" variant="gold" className="h-7 text-xs gap-1 px-2" onClick={() => { setDisburseTarget(c); setAccount("Bank BCA"); setDisburseMode("full"); setDisburseAmount(""); }} data-testid={`cashback-disburse-${c.id}`}>
                      <Gift className="h-3 w-3" /> {c.cashback_status === "Dicairkan Sebagian" ? "Cairkan Sisa" : "Cairkan"}
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 px-2 text-rose-600 hover:text-rose-700" onClick={() => { setCancelTarget(c); setReason(""); }} data-testid={`cashback-cancel-${c.id}`}>
                    <XCircle className="h-3 w-3" /> Batalkan
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>

      <Dialog open={!!disburseTarget} onOpenChange={(o) => !o && setDisburseTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Cairkan Cashback</DialogTitle>
            <DialogDescription>Pencairan akan mengurangi kas/bank dan dicatat pada riwayat pencairan.</DialogDescription>
          </DialogHeader>
          {disburseTarget && (
            <div className="space-y-2 py-1 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Pelanggan</span><span className="font-medium">{disburseTarget.customer_name}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">No. Order</span><span>#{disburseTarget.id.slice(-6).toUpperCase()}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Disetujui Oleh</span><span>{disburseTarget.cashback_approved_by || "-"}</span></div>
              <div className="flex justify-between font-semibold"><span>Nominal Cashback</span><span className="text-amber-600">{formatIDR(disburseTarget.cashback)}</span></div>
              {(disburseTarget.cashback_disbursed_amount || 0) > 0 && (
                <div className="flex justify-between text-xs"><span className="text-muted-foreground">Sudah Dicairkan</span><span>{formatIDR(disburseTarget.cashback_disbursed_amount)}</span></div>
              )}
              <div className="flex justify-between font-semibold text-xs"><span>Sisa Cashback</span><span className="text-amber-600">{formatIDR(disburseRemaining)}</span></div>
              <div className="space-y-1.5 pt-1">
                <Label>Jenis Pencairan</Label>
                <div className="grid grid-cols-2 gap-1.5">
                  <Button type="button" size="sm" variant={disburseMode === "full" ? "gold" : "outline"} className="h-8 text-xs" onClick={() => setDisburseMode("full")} data-testid="cashback-mode-full">Bayar Penuh</Button>
                  <Button type="button" size="sm" variant={disburseMode === "partial" ? "gold" : "outline"} className="h-8 text-xs" onClick={() => setDisburseMode("partial")} data-testid="cashback-mode-partial">Bayar Sebagian</Button>
                </div>
              </div>
              {disburseMode === "partial" && (
                <div className="space-y-1.5">
                  <Label>Nominal Pencairan Sebagian</Label>
                  <Input data-testid="cashback-partial-amount" type="number" min="1" value={disburseAmount} onChange={(e) => setDisburseAmount(e.target.value)} placeholder={`Maks ${formatIDR(disburseRemaining)}`} />
                </div>
              )}
              <div className="space-y-1.5 pt-1">
                <Label>Metode / Akun Pencairan</Label>
                <Select value={account} onValueChange={setAccount}>
                  <SelectTrigger data-testid="cashback-account"><SelectValue /></SelectTrigger>
                  <SelectContent>{ACCOUNTS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDisburseTarget(null)}>Batal</Button>
            <Button variant="gold" onClick={disburse} disabled={busy || (disburseMode === "partial" && !(parseFloat(disburseAmount) > 0))} data-testid="cashback-confirm">Cairkan Cashback</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!cancelTarget} onOpenChange={(o) => !o && setCancelTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Batalkan Cashback</DialogTitle>
            <DialogDescription>Gunakan bila pesanan dibatalkan. Cashback yang dibatalkan tidak menjadi kewajiban perusahaan.</DialogDescription>
          </DialogHeader>
          {cancelTarget && (
            <div className="space-y-3 py-1 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Pelanggan</span><span className="font-medium">{cancelTarget.customer_name}</span></div>
              <div className="flex justify-between font-semibold"><span>Nominal Cashback</span><span className="text-amber-600">{formatIDR(cancelTarget.cashback)}</span></div>
              <div className="space-y-1.5">
                <Label>Alasan Pembatalan <span className="text-rose-500">*</span></Label>
                <Textarea data-testid="cashback-cancel-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Contoh: Pesanan dibatalkan oleh pelanggan" rows={3} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelTarget(null)}>Kembali</Button>
            <Button variant="destructive" onClick={cancel} disabled={busy} data-testid="cashback-cancel-confirm">Batalkan Cashback</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
